import { useEffect, useRef } from "react";
import type { AppContext, AppEvent } from "@/types";

interface ScreenTrackingOptions {
  screenName: string;
  viewedEvent: AppEvent;
  exitedEvent?: AppEvent;
  ctx: AppContext | null;
  additionalAttributes?: Record<string, unknown>;
}

export function useScreenTracking({
  screenName,
  viewedEvent,
  exitedEvent,
  ctx,
  additionalAttributes = {},
}: ScreenTrackingOptions): void {
  const hasEmittedViewEvent = useRef(false);
  const mountTimeRef = useRef<number>(0);
  const ctxRef = useRef<AppContext | null>(null);

  useEffect(() => {
    ctxRef.current = ctx;
  }, [ctx]);

  useEffect(() => {
    if (!ctx || hasEmittedViewEvent.current) return;

    mountTimeRef.current = Date.now();

    ctx.sendEvent(viewedEvent, {
      screen_name: screenName,
      viewed_at: mountTimeRef.current,
      ...additionalAttributes,
    });

    hasEmittedViewEvent.current = true;

    return () => {
      if (exitedEvent && ctxRef.current) {
        const timeSpentMs = Date.now() - mountTimeRef.current;

        ctxRef.current.sendEvent(exitedEvent, {
          screen_name: screenName,
          time_spent_ms: timeSpentMs,
          time_spent_seconds: Math.round(timeSpentMs / 1000),
          exit_type: "navigation",
          exited_at: Date.now(),
          ...additionalAttributes,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx]);
}
